import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, CheckCircle, XCircle, 
  MessageSquare, Send, Filter, Search, BookOpen,
  Building2, Users, Globe, Clock, ThumbsUp, Share2,
  AlertCircle, ChevronDown, ChevronRight, Paperclip, X, Upload, FileText
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ORG_SIZES = ["MICRO", "SMALL", "MEDIUM", "LARGE", "ENTERPRISE"];

// ============== MAIN COMPONENT ==============
const BestPracticesManagementContent = () => {
  const [activeTab, setActiveTab] = useState('global');
  const [practices, setPractices] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPractice, setEditingPractice] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPractices();
    fetchQuestions();
    fetchPillars();
    fetchIndustries();
  }, []);

  const fetchPillars = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/pillars`);
      const data = await res.json();
      setPillars(data.pillars || []);
    } catch (err) {
      console.error('Failed to fetch pillars:', err);
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/industries`);
      const data = await res.json();
      setIndustries(data.industries || []);
    } catch (err) {
      console.error('Failed to fetch industries:', err);
    }
  };

  const fetchPractices = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/best-practices`);
      const data = await res.json();
      setPractices(data.practices || []);
    } catch (err) {
      console.error('Failed to fetch practices:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/best-practices/questions`);
      const data = await res.json();
      setQuestions(data.questions || []);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    }
  };

  const handleDelete = async (practiceId) => {
    if (!window.confirm('Are you sure you want to delete this best practice?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/best-practices/${practiceId}`, { method: 'DELETE' });
      fetchPractices();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleApprove = async (practiceId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/best-practices/${practiceId}/approve`, { method: 'POST' });
      fetchPractices();
    } catch (err) {
      console.error('Failed to approve:', err);
    }
  };

  const handleReject = async (practiceId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    try {
      await fetch(`${BACKEND_URL}/api/admin/best-practices/${practiceId}/reject?reason=${encodeURIComponent(reason || '')}`, { method: 'POST' });
      fetchPractices();
    } catch (err) {
      console.error('Failed to reject:', err);
    }
  };

  const globalPractices = practices.filter(p => p.type === 'global');
  const orgSubmissions = practices.filter(p => p.type === 'org_submitted');
  const pendingSubmissions = orgSubmissions.filter(p => p.status === 'pending');
  const pendingQuestions = questions.filter(q => q.status === 'pending');

  const filteredPractices = (activeTab === 'global' ? globalPractices : orgSubmissions)
    .filter(p => filterStatus === 'all' || p.status === filterStatus)
    .filter(p => 
      p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.pillar?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-gray-900">Best Practices Management</h1>
              <p className="text-gray-600 mt-1">Create, manage, and approve best practices</p>
            </div>
            <button
              onClick={() => { setEditingPractice(null); setShowCreateModal(true); }}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all"
              data-testid="create-best-practice-btn"
            >
              <Plus className="w-5 h-5" />
              Create Best Practice
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-indigo-50 rounded-xl p-4">
              <p className="text-indigo-600 font-bold text-2xl">{globalPractices.length}</p>
              <p className="text-indigo-800 text-sm">Global Best Practices</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-amber-600 font-bold text-2xl">{pendingSubmissions.length}</p>
              <p className="text-amber-800 text-sm">Pending Approvals</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-green-600 font-bold text-2xl">{orgSubmissions.filter(p => p.status === 'published').length}</p>
              <p className="text-green-800 text-sm">Org Submissions Approved</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-purple-600 font-bold text-2xl">{pendingQuestions.length}</p>
              <p className="text-purple-800 text-sm">Questions Pending</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-6 border-b border-gray-200">
            {[
              { id: 'global', label: 'Global Best Practices', icon: Globe },
              { id: 'org', label: 'Organization Submissions', icon: Building2, badge: pendingSubmissions.length },
              { id: 'questions', label: 'Questions & Responses', icon: MessageSquare, badge: pendingQuestions.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-semibold border-b-4 transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab !== 'questions' && (
          <>
            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search best practices..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Empty State */}
            {filteredPractices.length === 0 && !loading && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-600 mb-2">No Best Practices Yet</h3>
                <p className="text-gray-500 mb-6">
                  {activeTab === 'global' 
                    ? 'Create your first global best practice to get started.'
                    : 'No organization submissions to review.'}
                </p>
                {activeTab === 'global' && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700"
                  >
                    Create Best Practice
                  </button>
                )}
              </div>
            )}

            {/* Practices List */}
            <div className="space-y-4">
              {filteredPractices.map(practice => (
                <PracticeCard
                  key={practice.id}
                  practice={practice}
                  onEdit={() => { setEditingPractice(practice); setShowCreateModal(true); }}
                  onDelete={() => handleDelete(practice.id)}
                  onApprove={() => handleApprove(practice.id)}
                  onReject={() => handleReject(practice.id)}
                  onView={() => setSelectedPractice(practice)}
                />
              ))}
            </div>
          </>
        )}

        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <QuestionsPanel questions={questions} onRefresh={fetchQuestions} />
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateEditModal
          practice={editingPractice}
          pillars={pillars}
          industries={industries}
          onClose={() => { setShowCreateModal(false); setEditingPractice(null); }}
          onSave={() => { fetchPractices(); setShowCreateModal(false); setEditingPractice(null); }}
        />
      )}

      {/* View Detail Modal */}
      {selectedPractice && (
        <DetailModal
          practice={selectedPractice}
          onClose={() => setSelectedPractice(null)}
        />
      )}
    </div>
  );
};


// ============== PRACTICE CARD ==============
const PracticeCard = ({ practice, onEdit, onDelete, onApprove, onReject, onView }) => {
  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    published: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{practice.title}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[practice.status]}`}>
              {practice.status}
            </span>
            {practice.type === 'org_submitted' && (
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                Org Submission
              </span>
            )}
          </div>
          
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{practice.description}</p>
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              {practice.pillar}
            </span>
            {practice.organization_size && (
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {Array.isArray(practice.organization_size) 
                  ? practice.organization_size.join(', ')
                  : practice.organization_size}
              </span>
            )}
            {practice.organization_name && (
              <span className="flex items-center gap-1 text-purple-600">
                <Users className="w-4 h-4" />
                {practice.organization_name}
              </span>
            )}
          </div>

          {/* Metrics */}
          {practice.metrics && (
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="flex items-center gap-1 text-gray-500">
                <Eye className="w-4 h-4" /> {practice.metrics.views || 0}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                <ThumbsUp className="w-4 h-4" /> {practice.metrics.likes || 0}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                <Share2 className="w-4 h-4" /> {practice.metrics.shares || 0}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onView}
            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={onEdit}
            className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
            title="Edit"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          {practice.type === 'org_submitted' && practice.status === 'pending' && (
            <>
              <button
                onClick={onApprove}
                className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                title="Approve"
              >
                <CheckCircle className="w-5 h-5" />
              </button>
              <button
                onClick={onReject}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                title="Reject"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={onDelete}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};


// ============== CREATE/EDIT MODAL ==============
const CreateEditModal = ({ practice, pillars, industries, onClose, onSave }) => {
  const defaultPillar = pillars.length > 0 ? pillars[0].name : '';
  const defaultSubPillar = pillars.length > 0 && pillars[0].sub_pillars?.length > 0 
    ? (pillars[0].sub_pillars[0].title || pillars[0].sub_pillars[0]) 
    : '';

  const [formData, setFormData] = useState({
    title: practice?.title || '',
    description: practice?.description || '',
    pillar: practice?.pillar || defaultPillar,
    sub_pillar: practice?.sub_pillar || defaultSubPillar,
    organization_size: practice?.organization_size || ['MICRO'],
    industry: practice?.industry || '',
    implementation_steps: practice?.implementation_steps || [''],
    expected_outcomes: practice?.expected_outcomes || [''],
    resources_needed: practice?.resources_needed || '',
    timeframe: practice?.timeframe || '',
    status: practice?.status || 'draft',
    attachments: practice?.attachments || []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);

  const selectedPillar = pillars.find(p => p.name === formData.pillar);
  const subPillars = selectedPillar?.sub_pillars?.map(sp => 
    typeof sp === 'object' ? sp.title : sp
  ) || [];

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedIds = [];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds 10MB limit`);
        continue;
      }

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('entity_type', 'best_practice');
      formDataUpload.append('entity_id', practice?.id || 'temp');

      try {
        const res = await fetch(`${BACKEND_URL}/api/attachments/upload`, {
          method: 'POST',
          body: formDataUpload
        });

        if (res.ok) {
          const data = await res.json();
          uploadedIds.push(data.attachment_id);
          setAttachmentFiles(prev => [...prev, { id: data.attachment_id, filename: file.name, size: file.size }]);
        } else {
          setError(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        setError(`Error uploading ${file.name}`);
      }
    }

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...uploadedIds]
    }));
    setUploading(false);
  };

  const removeAttachment = async (attachmentId) => {
    try {
      await fetch(`${BACKEND_URL}/api/attachments/${attachmentId}`, { method: 'DELETE' });
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter(id => id !== attachmentId)
      }));
      setAttachmentFiles(prev => prev.filter(f => f.id !== attachmentId));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      setError('Title and description are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        implementation_steps: formData.implementation_steps.filter(s => s.trim()),
        expected_outcomes: formData.expected_outcomes.filter(o => o.trim())
      };

      const url = practice 
        ? `${BACKEND_URL}/api/admin/best-practices/${practice.id}`
        : `${BACKEND_URL}/api/admin/best-practices`;
      
      const method = practice ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        onSave();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => setFormData({ ...formData, implementation_steps: [...formData.implementation_steps, ''] });
  const addOutcome = () => setFormData({ ...formData, expected_outcomes: [...formData.expected_outcomes, ''] });

  const updateStep = (index, value) => {
    const steps = [...formData.implementation_steps];
    steps[index] = value;
    setFormData({ ...formData, implementation_steps: steps });
  };

  const updateOutcome = (index, value) => {
    const outcomes = [...formData.expected_outcomes];
    outcomes[index] = value;
    setFormData({ ...formData, expected_outcomes: outcomes });
  };

  const toggleSize = (size) => {
    const sizes = formData.organization_size.includes(size)
      ? formData.organization_size.filter(s => s !== size)
      : [...formData.organization_size, size];
    setFormData({ ...formData, organization_size: sizes });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <h2 className="text-2xl font-black">
            {practice ? 'Edit Best Practice' : 'Create Global Best Practice'}
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter best practice title..."
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Describe this best practice..."
            />
          </div>

          {/* Pillar & Sub-Pillar - FROM DATABASE */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Pillar</label>
              <select
                value={formData.pillar}
                onChange={(e) => {
                  const pillar = pillars.find(p => p.name === e.target.value);
                  const firstSubPillar = pillar?.sub_pillars?.[0];
                  setFormData({ 
                    ...formData, 
                    pillar: e.target.value,
                    sub_pillar: typeof firstSubPillar === 'object' ? firstSubPillar.title : firstSubPillar || ''
                  });
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {pillars.map(p => (
                  <option key={p.pillar_id || p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sub-Pillar</label>
              <select
                value={formData.sub_pillar}
                onChange={(e) => setFormData({ ...formData, sub_pillar: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {subPillars.map(sp => (
                  <option key={sp} value={sp}>{sp}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Organization Sizes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Organization Sizes</label>
            <div className="flex flex-wrap gap-2">
              {ORG_SIZES.map(size => (
                <button
                  key={size}
                  type="button"
                  onClick={() => toggleSize(size)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    formData.organization_size.includes(size)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Industry & Timeframe */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
              <select
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select Industry...</option>
                {industries.map(ind => (
                  <option key={ind.id} value={ind.name}>{ind.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Timeframe</label>
              <input
                type="text"
                value={formData.timeframe}
                onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., 3-6 months"
              />
            </div>
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Paperclip className="inline w-4 h-4 mr-1" />
              Attachments (Max 10MB each)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="attachment-upload"
              />
              <label
                htmlFor="attachment-upload"
                className="flex items-center justify-center gap-2 cursor-pointer text-gray-600 hover:text-indigo-600"
              >
                {uploading ? (
                  <span>Uploading...</span>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Click to upload files</span>
                  </>
                )}
              </label>
              
              {/* Uploaded files list */}
              {attachmentFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {attachmentFiles.map(file => (
                    <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">{file.filename}</span>
                        <span className="text-xs text-gray-400">({Math.round(file.size / 1024)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(file.id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Implementation Steps */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Implementation Steps</label>
            {formData.implementation_steps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <span className="text-gray-400 font-mono">{idx + 1}.</span>
                <input
                  type="text"
                  value={step}
                  onChange={(e) => updateStep(idx, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter implementation step..."
                />
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              className="text-indigo-600 text-sm font-semibold hover:text-indigo-800"
            >
              + Add Step
            </button>
          </div>

          {/* Expected Outcomes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Outcomes</label>
            {formData.expected_outcomes.map((outcome, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <span className="text-gray-400">•</span>
                <input
                  type="text"
                  value={outcome}
                  onChange={(e) => updateOutcome(idx, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter expected outcome..."
                />
              </div>
            ))}
            <button
              type="button"
              onClick={addOutcome}
              className="text-indigo-600 text-sm font-semibold hover:text-indigo-800"
            >
              + Add Outcome
            </button>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (practice ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============== QUESTIONS PANEL ==============
const QuestionsPanel = ({ questions, onRefresh }) => {
  const [responding, setResponding] = useState(null);
  const [responseText, setResponseText] = useState('');

  const handleRespond = async (questionId) => {
    if (!responseText.trim()) return;

    try {
      await fetch(`${BACKEND_URL}/api/admin/best-practices/questions/${questionId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_id: questionId, response_text: responseText })
      });
      setResponding(null);
      setResponseText('');
      onRefresh();
    } catch (err) {
      console.error('Failed to respond:', err);
    }
  };

  if (questions.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
        <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-600 mb-2">No Questions Yet</h3>
        <p className="text-gray-500">Questions from organizations will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {questions.map(q => (
        <div key={q.id} className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  q.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                }`}>
                  {q.status === 'pending' ? 'Pending Response' : 'Answered'}
                </span>
                <span className="text-gray-500 text-sm">
                  from <strong>{q.organization_name}</strong>
                </span>
              </div>

              <p className="text-gray-500 text-sm mb-2">
                On: <strong>{q.best_practice_title}</strong>
              </p>

              <p className="text-gray-900 font-medium mb-4">{q.question_text}</p>

              {q.response && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-green-800 font-semibold mb-1">Admin Response:</p>
                  <p className="text-green-900">{q.response}</p>
                </div>
              )}

              {responding === q.id && (
                <div className="mt-4">
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Type your response..."
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => { setResponding(null); setResponseText(''); }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleRespond(q.id)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Send Response
                    </button>
                  </div>
                </div>
              )}
            </div>

            {q.status === 'pending' && responding !== q.id && (
              <button
                onClick={() => setResponding(q.id)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Respond
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};


// ============== DETAIL MODAL ==============
const DetailModal = ({ practice, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black">{practice.title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
          <div className="flex items-center gap-4 mt-3 text-indigo-100">
            <span>{practice.pillar}</span>
            <span>•</span>
            <span>{practice.sub_pillar}</span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="font-bold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-700">{practice.description}</p>
          </div>

          {practice.implementation_steps?.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Implementation Steps</h3>
              <ol className="list-decimal list-inside space-y-2">
                {practice.implementation_steps.map((step, idx) => (
                  <li key={idx} className="text-gray-700">{step}</li>
                ))}
              </ol>
            </div>
          )}

          {practice.expected_outcomes?.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Expected Outcomes</h3>
              <ul className="list-disc list-inside space-y-2">
                {practice.expected_outcomes.map((outcome, idx) => (
                  <li key={idx} className="text-gray-700">{outcome}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-500 text-sm">Organization Sizes</p>
              <p className="font-semibold">{practice.organization_size?.join(', ') || 'All'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-500 text-sm">Timeframe</p>
              <p className="font-semibold">{practice.timeframe || 'Not specified'}</p>
            </div>
          </div>

          {practice.metrics && (
            <div className="flex items-center gap-6 pt-4 border-t">
              <span className="flex items-center gap-2 text-gray-600">
                <Eye className="w-5 h-5" /> {practice.metrics.views} views
              </span>
              <span className="flex items-center gap-2 text-gray-600">
                <ThumbsUp className="w-5 h-5" /> {practice.metrics.likes} likes
              </span>
              <span className="flex items-center gap-2 text-gray-600">
                <Share2 className="w-5 h-5" /> {practice.metrics.shares} shares
              </span>
            </div>
          )}
        </div>

        <div className="p-6 border-t">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Wrapper with SystemAdminLayout
const BestPracticesManagement = () => {
  return (
    <SystemAdminLayout>
      <BestPracticesManagementContent />
    </SystemAdminLayout>
  );
};

export default BestPracticesManagement;
